/*
  <cart-page> — the standalone /cart page for the alife theme.

  The design twin of <cart-drawer> (assets/cart-drawer.js): same Cart Ajax API
  mutations (quantity steppers, remove, note, discount permalink) and the same
  Section Rendering API re-render, but laid out as a full page instead of a
  slide-in panel.

  Coordination with the drawer (both are on the page at once):
    - After every mutation this dispatches `cart:updated` with
      `detail.source = 'cart-page'` so the header count stays live.
    - It also LISTENS for `cart:updated` from anywhere else (e.g. the drawer,
      which can be opened over this page from the header icon) and re-renders
      itself so the page never goes stale — its own events are ignored via the
      source guard.

  Degrades gracefully: without JS the checkout button submits the native /cart
  form; steppers/remove/note/discount need JS (same trade-off as the drawer).
*/
(function () {
  if (customElements.get('cart-page')) return;

  var routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function cartUrl(path) {
    return (routes + path).replace(/\/\/+/g, '/');
  }

  customElements.define('cart-page', class CartPage extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.noteTimer = null;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', this.onInput.bind(this));

      // The drawer (or anything else) changed the cart → refresh this page.
      this.onCartUpdated = function (event) {
        if (event.detail && event.detail.source === 'cart-page') return;
        this.refresh();
      }.bind(this);
      document.addEventListener('cart:updated', this.onCartUpdated);
    }

    disconnectedCallback() {
      clearTimeout(this.noteTimer);
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }

    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* ---- Event delegation ---- */
    onClick(event) {
      var panelToggle = event.target.closest('[data-cart-panel-toggle]');
      if (panelToggle) { this.togglePanel(panelToggle.dataset.cartPanelToggle); return; }

      var apply = event.target.closest('[data-cart-discount-apply]');
      if (apply) { this.applyDiscount(); return; }

      var line = event.target.closest('[data-cart-line]');
      if (!line) return;
      var key = line.dataset.lineKey;
      var current = parseInt(line.querySelector('[data-qty-value]').textContent, 10) || 0;

      if (event.target.closest('[data-qty-up]')) { this.changeLine(key, current + 1); return; }
      if (event.target.closest('[data-qty-down]')) { this.changeLine(key, current - 1); return; }
      if (event.target.closest('[data-cart-remove]')) { this.changeLine(key, 0); return; }
    }

    onInput(event) {
      // Consent checkboxes: persist immediately so the choice survives
      // re-renders and lands on the order (same behaviour as the drawer).
      if (event.target.matches('[data-cart-consent]')) {
        this.saveConsent(event.target);
        return;
      }
      if (!event.target.matches('[data-cart-note]')) return;
      clearTimeout(this.noteTimer);
      var value = event.target.value;
      this.noteTimer = setTimeout(function () { this.saveNote(value); }.bind(this), 500);
    }

    saveConsent(input) {
      var match = /^attributes\[(.+)\]$/.exec(input.name || '');
      if (!match) return;
      var attributes = {};
      attributes[match[1]] = input.checked ? input.value || 'Taip' : '';
      fetch(cartUrl('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).catch(function () {});
    }

    /* ---- Accordion panels (note / delivery / discount) — same WAAPI recipe
       as the drawer so the two feel identical. ---- */
    togglePanel(name) {
      var self = this;
      var panels = Array.prototype.slice.call(this.querySelectorAll('[data-cart-panel]'));
      var target = panels.filter(function (p) { return p.dataset.cartPanel === name; })[0];
      if (!target) return;

      var willOpen = target.hidden;

      panels.forEach(function (panel) {
        if (panel === target) {
          self.animatePanel(panel, willOpen);
        } else if (!panel.hidden) {
          self.animatePanel(panel, false);
        }
      });

      this.querySelectorAll('[data-cart-panel-toggle]').forEach(function (btn) {
        btn.classList.toggle('is-active', willOpen && btn.dataset.cartPanelToggle === name);
      });
    }

    animatePanel(panel, open) {
      panel.getAnimations().forEach(function (a) { a.cancel(); });

      if (this.prefersReducedMotion()) {
        panel.hidden = !open;
        if (open) this.focusPanel(panel);
        return;
      }

      var cs = window.getComputedStyle(panel);
      var padTop = cs.paddingTop;
      var padBottom = cs.paddingBottom;
      var easing = 'cubic-bezier(0.23, 1, 0.32, 1)';

      if (open) {
        panel.hidden = false;
        var full = panel.scrollHeight;
        panel.style.overflow = 'hidden';
        var anim = panel.animate(
          [
            { height: '0px', opacity: 0, paddingTop: '0px', paddingBottom: '0px' },
            { height: full + 'px', opacity: 1, paddingTop: padTop, paddingBottom: padBottom }
          ],
          { duration: 280, easing: easing }
        );
        anim.finished.then(function () { panel.style.overflow = ''; panel.style.height = ''; }).catch(function () {});
        this.focusPanel(panel);
      } else {
        var current = panel.offsetHeight;
        panel.style.overflow = 'hidden';
        var anim2 = panel.animate(
          [
            { height: current + 'px', opacity: 1, paddingTop: padTop, paddingBottom: padBottom },
            { height: '0px', opacity: 0, paddingTop: '0px', paddingBottom: '0px' }
          ],
          { duration: 240, easing: easing }
        );
        anim2.finished.then(function () { panel.hidden = true; panel.style.overflow = ''; panel.style.height = ''; }).catch(function () {});
      }
    }

    focusPanel(panel) {
      var field = panel.querySelector('textarea, input');
      if (field) setTimeout(function () { field.focus({ preventScroll: true }); }, 60);
    }

    /* ---- Cart Ajax API ---- */
    changeLine(key, quantity) {
      this.setBusy(true);
      fetch(cartUrl('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity, sections: this.sectionId, sections_url: window.location.pathname })
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          this.renderSections(cart && cart.sections);
        }.bind(this))
        .catch(function () {})
        .finally(function () { this.setBusy(false); }.bind(this));
    }

    saveNote(value) {
      fetch(cartUrl('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value })
      }).catch(function () {});
    }

    applyDiscount() {
      var input = this.querySelector('[data-cart-discount]');
      if (!input) return;
      var code = input.value.trim();
      if (!code) return;
      window.location.href = cartUrl('discount/' + encodeURIComponent(code)) + '?redirect=/cart';
    }

    /* ---- Re-render via the Section Rendering API ---- */
    renderSections(sections) {
      var html = sections && (sections[this.sectionId] || sections['main-cart']);
      if (html) {
        this.swap(html);
        this.broadcast();
        return;
      }
      this.refresh();
    }

    refresh() {
      fetch(window.location.pathname + '?sections=' + this.sectionId, { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var html = data && (data[this.sectionId] || data['main-cart']);
          if (html) this.swap(html);
        }.bind(this))
        .catch(function () {});
    }

    swap(html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = parsed.querySelector('[data-cart-page-content]');
      var current = this.querySelector('[data-cart-page-content]');
      if (fresh && current) current.replaceWith(fresh);
    }

    broadcast() {
      var content = this.querySelector('[data-cart-page-content]');
      var count = content ? parseInt(content.dataset.itemCount, 10) || 0 : 0;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: count, source: 'cart-page' } }));
    }

    setBusy(state) {
      this.toggleAttribute('data-busy', !!state);
    }
  });
})();
